import { create } from 'zustand'
import type {
  ViewConfig,
  ViewSort,
  ViewGrouping,
  ViewGroupSort,
  GroupStateConfig,
  GroupLevelStateConfig,
  ViewColumnConfig,
  FooterAggregationConfig,
  AggregationType,
  FilterGroup,
} from '@features/views/models'
import { emptyFilterGroup } from '@features/views/logic/filters'
import { pinnedColumnConfigs } from '@features/columns/logic/columnPinning'
import { RECORD_WINDOW_LIMIT } from '../../../constants'
import {
  removeColumnFromFilters,
  removeColumnFromGrouping,
  removeColumnFromViewConfig,
} from '../logic/removeColumnReferences'

/**
 * Deep-clone a filter tree so loaded views don't share references with the
 * source ViewConfig. JSON round-trip is safe here — leaf values are always
 * JSON-serializable (number/string/boolean/null) because they round-trip
 * through the backend.
 */
function cloneFilterGroup(group: FilterGroup): FilterGroup {
  return JSON.parse(JSON.stringify(group)) as FilterGroup
}

/**
 * Compare two group_id dicts for equality.
 * Keys are column_ids.
 */
function groupIdEquals(
  a: { [column_id: string]: unknown | null },
  b: { [column_id: string]: unknown | null }
): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false
  }
  return true
}

function normalizeGroupIdForGrouping(
  groupId: { [column_id: string]: unknown | null },
  grouping: ViewGrouping[]
): { [column_id: string]: unknown | null } {
  const normalized: { [column_id: string]: unknown | null } = {}
  const keys = Object.keys(groupId)

  for (let idx = 0; idx < keys.length; idx += 1) {
    const columnId = grouping[idx]?.column_id
    if (columnId && columnId in groupId) {
      normalized[columnId] = groupId[columnId]
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : { ...groupId }
}

function normalizeGroupStatesForGrouping(
  groupStates: GroupStateConfig[],
  grouping: ViewGrouping[]
): GroupStateConfig[] {
  return groupStates.map((state) => ({
    ...state,
    group_id: normalizeGroupIdForGrouping(state.group_id, grouping),
  }))
}

function getGroupStateLevelColumnId(
  groupState: GroupStateConfig,
  grouping: ViewGrouping[]
): string | undefined {
  return grouping[Object.keys(groupState.group_id).length - 1]?.column_id
}

interface ViewState {
  /** The view the user started from (null = default / All Records) */
  originView: ViewConfig | null
  /** The temp working copy (null = no edits, viewing origin/default directly) */
  tempView: ViewConfig | null

  /** Current local config — always reflects what the user sees.
   * `filters` is a recursive tree; an empty `conditions` array means "no
   * filters". The previous flat-array shape lost OR / nested-group
   * structure on save. */
  filters: FilterGroup
  sorts: ViewSort[]
  grouping: ViewGrouping[]
  /** Per-group expansion overrides (persisted to backend on auto-sync). */
  groupStates: GroupStateConfig[]
  /** Per-level expansion overrides (persisted to backend on auto-sync). */
  groupLevelStates: GroupLevelStateConfig[]
  /** Per-column config (pinning, etc.) — sparse; persisted to backend on auto-sync. */
  columnConfigs: ViewColumnConfig[]
  footerAggregations: FooterAggregationConfig[]

  /** Local-only expand/collapse state — derived from group_states for instant UI feedback. */
  expandedGroups: Set<string | number>
  offset: number
  limit: number

  /** Load a saved/permanent view as the origin (clears any temp) */
  loadView: (view: ViewConfig) => void
  /** Replace the origin with a newer copy of the same view (e.g. after its
   *  settings change) while keeping any temp edits; without a temp view this
   *  is `loadView`. */
  refreshOriginView: (view: ViewConfig) => void
  /** Store the temp view after auto-promote POST */
  setTempView: (view: ViewConfig) => void
  /** Clear temp after save completes (temp→permanent or save-to-origin) */
  clearTemp: () => void
  /** Load a temp view from the manage modal (sets both origin lookup and temp) */
  loadTempView: (temp: ViewConfig, origin: ViewConfig | null) => void

  setFilters: (filters: FilterGroup) => void
  clearFilters: () => void
  setSorts: (sorts: ViewSort[]) => void
  addSort: (sort: ViewSort) => void
  removeSort: (index: number) => void
  reorderSorts: (fromIndex: number, toIndex: number) => void
  updateSort: (index: number, sort: ViewSort) => void
  clearSorts: () => void
  setGrouping: (grouping: ViewGrouping[]) => void
  reorderGrouping: (fromIndex: number, toIndex: number) => void
  updateGroupingItem: (index: number, item: ViewGrouping) => void
  /** Set a level's sorts; which groups are open stays as it was. */
  setGroupSort: (index: number, sortBy: ViewGroupSort[]) => void
  removeGroupingItem: (index: number) => void
  clearGrouping: () => void

  /**
   * Toggle expand/collapse on a single group.
   * - `groupKey`: local-only key from `groupIdToKey()` — used for instant UI feedback.
   * - `groupId`: optional dict payload — when provided, also updates `group_states`
   *   for backend persistence. Callers that don't need persistence (e.g. tests,
   *   legacy numeric IDs) can omit it.
   */
  toggleGroupExpand: (
    groupKey: string | number,
    groupId?: { [column_id: string]: unknown | null }
  ) => void
  /** Set which groups are expanded on screen; used when hydrating from group state. */
  expandAllGroups: (groupIds: Array<string | number>) => void
  /** Expand every group as a user action (sets each active level to expanded, clears per-group overrides). */
  expandAllLevels: (groupIds: Array<string | number>) => void
  /** Collapse all groups (sets each active level to collapsed, clears per-group overrides). */
  collapseAllGroups: () => void
  /** Expand all groups at a given level; also removes conflicting per-group collapses at that level. */
  expandLevel: (columnId: string) => void
  /** Collapse all groups at a given level; also removes conflicting per-group expands at that level. */
  collapseLevel: (columnId: string) => void

  /** Replace the entire columnConfigs list (usually after a pin/unpin UI action). */
  setColumnConfigs: (configs: ViewColumnConfig[]) => void
  /**
   * Pin or unpin a column. Upserts into columnConfigs by column_id; an unpin
   * (`isPinned = false`) drops the entry entirely so the sparse list stays tidy.
   */
  setColumnPin: (columnId: string, isPinned: boolean) => void
  setAggregationForColumn: (columnId: string, fn: AggregationType | null) => void
  /**
   * Drop a deleted column from the working and stored views. Pass the columns
   * currently shown, in order: `tempView.column_ids` is a snapshot from its
   * creation, and column hydration rebuilds the layout from whatever ids land here.
   */
  removeColumnReferences: (columnId: string, visibleColumnIds?: string[]) => void

  discardChanges: () => void
  setPage: (offset: number) => void
  reset: () => void
}

/**
 * Resolve the default expand state for a group given the view config's precedence rules.
 * Used during hydration on loadView.
 */
function resolveInitialExpandState(
  grouping: ViewGrouping[],
  groupStates: GroupStateConfig[],
  groupLevelStates: GroupLevelStateConfig[]
): Set<string | number> {
  // On initial load we don't know which actual group_ids exist in the data yet —
  // the auto-expand effect in TablesPage populates expandedGroups once data arrives.
  // However, if there are persisted per-group collapse overrides, we honor them
  // by NOT adding their keys. The auto-expand logic in TablesPage uses
  // collectAllGroupKeysFromViewRows → expandAllGroups(), so it will overwrite anyway.
  // We still return empty here; precedence is resolved on each render via selectors.
  void grouping
  void groupStates
  void groupLevelStates
  return new Set()
}

export const useViewStore = create<ViewState>((set, get) => ({
  originView: null,
  tempView: null,
  filters: emptyFilterGroup(),
  sorts: [],
  grouping: [],
  groupStates: [],
  groupLevelStates: [],
  columnConfigs: [],
  footerAggregations: [],
  expandedGroups: new Set(),
  offset: 0,
  limit: RECORD_WINDOW_LIMIT,

  loadView: (view) =>
    set({
      originView: view,
      tempView: null,
      filters: cloneFilterGroup(view.filters),
      sorts: [...view.sorts],
      grouping: [...view.grouping],
      groupStates: normalizeGroupStatesForGrouping(view.group_states ?? [], view.grouping),
      groupLevelStates: [...(view.group_level_states ?? [])],
      columnConfigs: pinnedColumnConfigs(view.column_configs),
      footerAggregations: [...(view.footer_aggregations ?? [])],
      expandedGroups: resolveInitialExpandState(
        view.grouping,
        view.group_states ?? [],
        view.group_level_states ?? []
      ),
      offset: 0,
    }),

  refreshOriginView: (view) => {
    if (get().tempView) {
      set({ originView: view })
      return
    }
    get().loadView(view)
  },

  setTempView: (view) =>
    set({
      tempView: view,
      filters: cloneFilterGroup(view.filters),
      sorts: [...view.sorts],
      grouping: [...view.grouping],
      groupStates: normalizeGroupStatesForGrouping(view.group_states ?? [], view.grouping),
      groupLevelStates: [...(view.group_level_states ?? [])],
      columnConfigs: pinnedColumnConfigs(view.column_configs),
      footerAggregations: [...(view.footer_aggregations ?? [])],
      offset: 0,
    }),

  loadTempView: (temp, origin) =>
    set({
      originView: origin,
      tempView: temp,
      filters: cloneFilterGroup(temp.filters),
      sorts: [...temp.sorts],
      grouping: [...temp.grouping],
      groupStates: normalizeGroupStatesForGrouping(temp.group_states ?? [], temp.grouping),
      groupLevelStates: [...(temp.group_level_states ?? [])],
      columnConfigs: pinnedColumnConfigs(temp.column_configs),
      footerAggregations: [...(temp.footer_aggregations ?? [])],
      expandedGroups: resolveInitialExpandState(
        temp.grouping,
        temp.group_states ?? [],
        temp.group_level_states ?? []
      ),
      offset: 0,
    }),

  clearTemp: () =>
    set({
      tempView: null,
    }),

  setFilters: (filters) => set({ filters }),
  clearFilters: () => set({ filters: emptyFilterGroup() }),

  setSorts: (sorts) => set({ sorts }),
  addSort: (sort) => set((state) => ({ sorts: [...state.sorts, sort] })),

  removeSort: (index) => set((state) => ({ sorts: state.sorts.filter((_, i) => i !== index) })),

  reorderSorts: (fromIndex, toIndex) =>
    set((state) => {
      const sorts = [...state.sorts]
      const [moved] = sorts.splice(fromIndex, 1)
      sorts.splice(toIndex, 0, moved)
      return { sorts }
    }),

  updateSort: (index, sort) =>
    set((state) => ({ sorts: state.sorts.map((s, i) => (i === index ? sort : s)) })),

  clearSorts: () => set({ sorts: [] }),

  // Changing the grouping columns invalidates any existing per-group/per-level state —
  // clear them so we don't send stale entries to the backend.
  setGrouping: (grouping) => set({ grouping, groupStates: [], groupLevelStates: [] }),

  reorderGrouping: (fromIndex, toIndex) =>
    set((state) => {
      const grouping = [...state.grouping]
      const [moved] = grouping.splice(fromIndex, 1)
      grouping.splice(toIndex, 0, moved)
      return { grouping, groupStates: [], groupLevelStates: [] }
    }),

  updateGroupingItem: (index, item) =>
    set((state) => ({
      grouping: state.grouping.map((g, i) => (i === index ? item : g)),
      groupStates: [],
      groupLevelStates: [],
    })),

  setGroupSort: (index, sortBy) =>
    set((state) => ({
      grouping: state.grouping.map((g, i) => (i === index ? { ...g, sort_by: sortBy } : g)),
    })),

  removeGroupingItem: (index) =>
    set((state) => ({
      grouping: state.grouping.filter((_, i) => i !== index),
      groupStates: [],
      groupLevelStates: [],
    })),

  clearGrouping: () => set({ grouping: [], groupStates: [], groupLevelStates: [] }),

  toggleGroupExpand: (groupKey, groupId) =>
    set((state) => {
      // Update local set for instant UI feedback
      const expanded = new Set(state.expandedGroups)
      const willBeExpanded = !expanded.has(groupKey)
      if (willBeExpanded) expanded.add(groupKey)
      else expanded.delete(groupKey)

      // Update groupStates for backend persistence. Only possible if caller provided groupId.
      // Per-group override at this specific group_id.
      let groupStates = state.groupStates
      if (groupId) {
        const normalizedGroupId = normalizeGroupIdForGrouping(groupId, state.grouping)
        const existingIdx = state.groupStates.findIndex((s) =>
          groupIdEquals(s.group_id, normalizedGroupId)
        )
        if (existingIdx >= 0) {
          groupStates = state.groupStates.map((s, i) =>
            i === existingIdx ? { ...s, is_expanded: willBeExpanded } : s
          )
        } else {
          groupStates = [
            ...state.groupStates,
            { group_id: normalizedGroupId, is_expanded: willBeExpanded },
          ]
        }
      }

      return { expandedGroups: expanded, groupStates }
    }),

  expandAllGroups: (groupIds) => set({ expandedGroups: new Set(groupIds) }),

  // Mirrors collapseAllGroups. Without the level states, the next hydration
  // from group state would re-collapse whatever was collapsed before.
  expandAllLevels: (groupIds) =>
    set((state) => ({
      expandedGroups: new Set(groupIds),
      groupLevelStates: state.grouping.map((g) => ({
        column_id: g.column_id,
        is_expanded: true,
      })),
      groupStates: [],
    })),

  collapseAllGroups: () =>
    set((state) => ({
      expandedGroups: new Set(),
      // Collapse every grouping level, clearing per-group overrides (spec precedence rule:
      // level-wide action supersedes any earlier per-group overrides at those levels).
      groupLevelStates: state.grouping.map((g) => ({
        column_id: g.column_id,
        is_expanded: false,
      })),
      groupStates: [],
    })),

  expandLevel: (columnId) =>
    set((state) => {
      // Precedence rule: when the user does a level-wide action, remove per-group
      // overrides AT THAT LEVEL so "most recent user intent wins".
      const groupStates = state.groupStates.filter((s) => {
        return getGroupStateLevelColumnId(s, state.grouping) !== columnId
      })
      const otherLevels = state.groupLevelStates.filter((s) => s.column_id !== columnId)
      const groupLevelStates = [...otherLevels, { column_id: columnId, is_expanded: true }]
      return { groupStates, groupLevelStates }
    }),

  collapseLevel: (columnId) =>
    set((state) => {
      const groupStates = state.groupStates.filter((s) => {
        return getGroupStateLevelColumnId(s, state.grouping) !== columnId
      })
      const otherLevels = state.groupLevelStates.filter((s) => s.column_id !== columnId)
      const groupLevelStates = [...otherLevels, { column_id: columnId, is_expanded: false }]
      return { groupStates, groupLevelStates }
    }),

  setColumnConfigs: (configs) => set({ columnConfigs: configs }),

  setColumnPin: (columnId, isPinned) =>
    set((state) => {
      const filtered = state.columnConfigs.filter((c) => c.column_id !== columnId)

      if (!isPinned) {
        return { columnConfigs: filtered }
      }

      const next: ViewColumnConfig = {
        column_id: columnId,
        is_pinned: true,
      }

      return { columnConfigs: [...filtered, next] }
    }),

  setAggregationForColumn: (columnId, fn) =>
    set((state) => {
      const withoutColumn = state.footerAggregations.filter((agg) => agg.column_id !== columnId)
      const footerAggregations = fn
        ? [...withoutColumn, { column_id: columnId, function: fn }]
        : withoutColumn

      const grouping = state.grouping.map((group) => {
        const current = group.aggregation_overrides ?? []
        const withoutGroupColumn = current.filter((agg) => agg.column_id !== columnId)

        return {
          ...group,
          aggregation_overrides: fn
            ? [...withoutGroupColumn, { column_id: columnId, function: fn }]
            : withoutGroupColumn,
        }
      })

      return { footerAggregations, grouping }
    }),

  removeColumnReferences: (columnId, visibleColumnIds) =>
    set((state) => {
      const groupingColumnRemoved = state.grouping.some((group) => group.column_id === columnId)
      const filters = removeColumnFromFilters(state.filters, columnId)
      const sorts = state.sorts.filter((sort) => sort.column_id !== columnId)
      const grouping = removeColumnFromGrouping(state.grouping, columnId)
      const groupStates = groupingColumnRemoved
        ? []
        : state.groupStates.filter((groupState) => !(columnId in groupState.group_id))
      const groupLevelStates = groupingColumnRemoved
        ? []
        : state.groupLevelStates.filter((groupState) => groupState.column_id !== columnId)
      const columnConfigs = state.columnConfigs.filter((config) => config.column_id !== columnId)
      const footerAggregations = state.footerAggregations.filter(
        (aggregation) => aggregation.column_id !== columnId
      )

      return {
        originView: state.originView
          ? removeColumnFromViewConfig(state.originView, columnId)
          : null,
        tempView: state.tempView
          ? {
              ...removeColumnFromViewConfig(state.tempView, columnId),
              column_ids: (visibleColumnIds ?? state.tempView.column_ids).filter(
                (id) => id !== columnId
              ),
              filters,
              sorts,
              grouping,
              group_states: groupStates,
              group_level_states: groupLevelStates,
              column_configs: columnConfigs,
              footer_aggregations: footerAggregations,
            }
          : null,
        filters,
        sorts,
        grouping,
        groupStates,
        groupLevelStates,
        columnConfigs,
        footerAggregations,
        expandedGroups: groupingColumnRemoved ? new Set() : state.expandedGroups,
      }
    }),

  discardChanges: () =>
    set((state) => {
      const baseline = state.originView
      return {
        tempView: null,
        filters: baseline ? cloneFilterGroup(baseline.filters) : emptyFilterGroup(),
        sorts: baseline ? [...baseline.sorts] : [],
        grouping: baseline ? [...baseline.grouping] : [],
        groupStates: baseline
          ? normalizeGroupStatesForGrouping(baseline.group_states ?? [], baseline.grouping)
          : [],
        groupLevelStates: baseline ? [...(baseline.group_level_states ?? [])] : [],
        columnConfigs: baseline ? pinnedColumnConfigs(baseline.column_configs) : [],
        footerAggregations: baseline ? [...(baseline.footer_aggregations ?? [])] : [],
        offset: 0,
      }
    }),

  setPage: (offset) => set({ offset }),

  reset: () =>
    set({
      originView: null,
      tempView: null,
      filters: emptyFilterGroup(),
      sorts: [],
      grouping: [],
      groupStates: [],
      groupLevelStates: [],
      columnConfigs: [],
      footerAggregations: [],
      expandedGroups: new Set(),
      offset: 0,
      limit: RECORD_WINDOW_LIMIT,
    }),
}))
